import { Check } from "lucide-react";

// Interpretation block for a current-structure section (strengths + next challenge; no grade / no ability claim).
function StructInterp({ interp }) {
  if (!interp) return null;
  return (
    <div className="mt-3 space-y-2 border-t border-stone-200/70 pt-3" data-testid="struct-interp">
      {(interp.strengths || []).length > 0 && (
        <div data-testid="interp-strengths">
          <p className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-emerald-700 mb-1">Strengths of this structure</p>
          <ul className="space-y-1">
            {interp.strengths.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-[13.5px] text-stone-800"><Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />{s}</li>
            ))}
          </ul>
        </div>
      )}
      {interp.what_could_be_stronger && (
        <div data-testid="interp-stronger">
          <p className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-amber-700 mb-1">What could be stronger</p>
          <p className="text-[13.5px] text-stone-800 leading-relaxed">{interp.what_could_be_stronger}</p>
        </div>
      )}
      {interp.next_challenge && (
        <div data-testid="interp-next">
          <p className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-[#8C3A2A] mb-1">Next structural challenge</p>
          <p className="text-[13.5px] text-stone-800 leading-relaxed">{interp.next_challenge}</p>
        </div>
      )}
    </div>
  );
}

// Flatten the part-whole thought tree (parent_id nesting) into an ordered, depth-tagged list via an
// iterative pre-order DFS — avoids a self-referential recursive component.
function flattenThought(nodes) {
  const all = Array.isArray(nodes) ? nodes : [];
  const childrenOf = (id) => all.filter((n) => (n.parent_id || "") === (id || ""));
  const roots = all.filter((n) => !n.parent_id);
  const out = [];
  const stack = roots.slice().reverse().map((n) => ({ node: n, depth: 0 }));
  const seen = new Set();
  while (stack.length) {
    const { node, depth } = stack.pop();
    if (seen.has(node.id)) continue;   // guard against malformed cycles
    seen.add(node.id);
    out.push({ node, depth });
    const kids = childrenOf(node.id);
    for (let i = kids.length - 1; i >= 0; i--) stack.push({ node: kids[i], depth: depth + 1 });
  }
  return out;
}

// REPAIR 11 · CURRENT STRUCTURAL ASSESSMENT — how THIS product/thinking is organized now
// (independent of any before→after developmental comparison). No grade, no ability claim.
// U = context-sensitive unit noun ("essay" | "paragraph").
export default function ReviewCurrentStructure({ cs, U }) {
  if (!cs || (!cs.essay && !cs.thought)) return null;
  return (
    <div className="space-y-5" data-testid="review-current-structure">
      <div>
        <p className="font-mono-panel text-[10px] uppercase tracking-[0.18em] text-stone-400 mb-1">Current structure</p>
        <h3 className="font-serif-display text-xl sm:text-2xl leading-snug text-stone-900">How this {U} is organized</h3>
        <p className="text-[12.5px] text-stone-500 mt-1">A description of how you organized this {U} and your thinking for it — not a grade or a judgment of your ability.</p>
      </div>

      {cs.essay && (
        <div className="rounded-lg border border-[#8C3A2A]/20 bg-white p-5" data-testid="review-essay-structure">
          <p className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-[#8C3A2A] mb-2">Your {U}&rsquo;s structure — organized for a reader</p>
          {cs.essay.thesis && (
            <div className="mb-3">
              <p className="text-[10px] uppercase tracking-wide text-stone-400">Controlling idea</p>
              <p className="text-[14px] text-stone-900 font-medium">{cs.essay.thesis}</p>
            </div>
          )}
          <p className="text-[11px] text-stone-400 mb-1.5" data-testid="essay-reader-order-note">Sections in the order a reader meets them</p>
          <ul className="space-y-1.5">
            {(cs.essay.parts || []).map((p, i) => (
              <li key={i} data-testid={`essay-part-${i}`} data-depth={p.depth || 0}
                  className="flex items-start gap-2 text-[14px] text-stone-800"
                  style={{ paddingLeft: (p.depth || 0) * 18 }}>
                <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${(p.depth || 0) >= 1 ? "bg-[#8C3A2A]/30 ring-1 ring-[#8C3A2A]/40" : "bg-[#8C3A2A]/50"}`} />
                <span><span className="text-[10px] uppercase tracking-wide text-stone-400 mr-2">{p.role}</span>{p.label}</span>
              </li>
            ))}
          </ul>
          <StructInterp interp={cs.essay.interpretation} />
        </div>
      )}

      {cs.thought && (
        <div className="rounded-lg border border-stone-200 bg-[#faf9f6] p-5" data-testid="review-thought-structure">
          <p className="font-mono-panel text-[10px] uppercase tracking-[0.16em] text-stone-500 mb-1">How you organized your thinking</p>
          <p className="text-[12px] text-stone-500 mb-3">Related to the {U} above, but a different view: how your ideas are grouped and connected in your thinking.</p>
          {cs.thought.thesis && <p className="text-[14px] text-stone-900 font-medium mb-2">{cs.thought.thesis}</p>}
          <ul className="space-y-1">
            {flattenThought(cs.thought.nodes).map(({ node, depth }) => (
              <li key={node.id} data-testid={`thought-node-${node.id}`} className="flex items-start gap-2 text-[13.5px] text-stone-800" style={{ paddingLeft: depth * 14 }}>
                <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${node.kind === "open" ? "bg-amber-400" : "bg-stone-400"}`} />
                <span>{node.text}{node.kind === "open" ? <span className="text-[11px] text-amber-600 ml-1">(open question)</span> : null}</span>
              </li>
            ))}
          </ul>
          {(cs.thought.relations || []).length > 0 && (
            <div className="mt-3 text-[12.5px] text-stone-600">
              <p className="text-[10px] uppercase tracking-wide text-stone-400 mb-1">Relationships you drew</p>
              <ul className="space-y-0.5">
                {cs.thought.relations.slice(0, 6).map((r, i) => (
                  <li key={i} data-testid={`thought-relation-${i}`}>&ldquo;{r.from}&rdquo; <span className="text-stone-400">{r.label}</span> &rarr; &ldquo;{r.to}&rdquo;</li>
                ))}
              </ul>
            </div>
          )}
          <StructInterp interp={cs.thought.interpretation} />
        </div>
      )}
    </div>
  );
}
